import { Directive, ElementRef, forwardRef, input, OnDestroy, output, Renderer2 } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

/** works Independently of any formGroup or formControl - just checks the inputs value against the given ValidatorFn and outputs the result to (isValid). */
@Directive({
  selector: '[validator]',
  host: {'(blur)': 'onBlur()'},
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ValidatorDirective),
      multi: true,
    },
  ],
})
export class ValidatorDirective implements Validator, OnDestroy {
  public readonly validatorFn = input.required<ValidatorFn | null | undefined>({
    alias: 'validator',
  });
  public readonly isValid = output<boolean>();

  private control?: AbstractControl;
  private errorDiv: HTMLElement | null = null;
  private lastValidState: boolean | null = null;
  private isEvaluating = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private localeResourceManager: LocaleResourceManager) {
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    if(!control || this.isEvaluating) {
      return null;
    }

    const validatorFn = this.validatorFn();

    if (!validatorFn) {
      this.setValidity(true);
      this.removeErrorDiv();
      this.renderer.removeClass(this.el.nativeElement, 'is-invalid');
      return null;
    }

    try {
      this.isEvaluating = true;
      const result = validatorFn(control);
      this.isEvaluating = false;
      if (result) {
        this.setValidity(false);
        if(control.touched) {
          this.renderer.addClass(this.el.nativeElement, 'is-invalid');
          this.showError(result);
        }
      } else {
        this.setValidity(true);
        this.renderer.removeClass(this.el.nativeElement, 'is-invalid');
        this.removeErrorDiv();
      }

      return result;
    } catch(e) {
      this.isEvaluating = false;
      Log.trace(e)
      return null;
    }

  }

  protected onBlur(): void {
    this.control?.updateValueAndValidity({onlySelf: true});
  }

  private setValidity(valid: boolean): void {
    if (this.lastValidState !== valid) {
      this.lastValidState = valid;
      this.isValid.emit(valid);
    }
  }

  private showError(errors: ValidationErrors): void {
    if (!this.errorDiv) {
      this.errorDiv = this.renderer.createElement('div');
      this.renderer.addClass(this.errorDiv, 'invalid-feedback');
      this.renderer.appendChild(
        this.el.nativeElement.parentNode,
        this.errorDiv
      );
    }

    let message = errors['messageKey'];
    let param = errors['param'];

    if(!message) {
      if(errors['required']) {
        message = 'global.label.legend_required_field';
      }
      else if(errors['email']) {
        message = 'validation.error.invalid_char';
      }
      else if(errors['maxlength']) {
        message = 'validation.error.too_many_chars';
      }
      else if(errors['exceedsMax']) {
        message = 'validation.error.exceeds_max';
      }
      else {
        message = 'unknown validation error: ' + JSON.stringify(errors);
      }
    }

    this.errorDiv!.textContent = param
      ? this.localeResourceManager.getValueByKey(message, {0: param})
      : this.localeResourceManager.getValueByKey(message);
  }

  private removeErrorDiv(): void {
    if (this.errorDiv) {
      this.renderer.removeChild(
        this.el.nativeElement.parentNode,
        this.errorDiv
      );
      this.errorDiv = null;
    }
  }

  public ngOnDestroy(): void {
    this.removeErrorDiv();
  }
}
